// One registry for every place involved in the wedding. It does two jobs:
//
//  1. Resolves the freeform LOCATION text on a calendar event to a name, a map
//     link and a stable accent colour — which is what colour-codes the
//     timeline and lets it tell parallel events apart.
//  2. Backs the "Where" list beside the schedule, for the places guests need
//     to find. That list is curated rather than derived from the events: the
//     timeline already says what happens where and when, so repeating it here
//     would just be the same information twice. What belongs here is how to
//     actually get to each place — plus places that never appear as a calendar
//     event at all, like the families' homes.
//
// ── FILL THESE IN ───────────────────────────────────────────────────────────
// `address` and `directions` are blank below and are simply omitted while
// empty — better a name on its own than a confident-looking placeholder that
// sends guests to the wrong street. `mapUrl` is worth adding for each: open
// the place in Google Maps, Share, Copy link. Without one, the panel falls
// back to a Maps *search* for the address, and omits the link entirely if
// there's no address either.
//
// `role` is what the place is for, shown above its name — useful where the
// name alone doesn't say ("Signal Military Club" vs "Melse"). `coords` is for
// places with no street address to search. Both are optional and omitted when
// empty.
//
// `label` is the one name a place has, and every surface says it in full — the
// Where panel and the timeline card alike. `initials` overrides the two letters
// on the timeline's colour tile, which are otherwise the first letter of the
// first two words; worth setting where those aren't the memorable ones.
//
// `coords` is load-bearing beyond the Where panel: the families' homes are
// located on the calendar by a dropped pin, so their events arrive as a bare
// latitude/longitude with no name in them at all. Those get matched against
// these same coordinates — see `venueAtCoords` below — which is what puts the
// home's name (and its colour, and its nesting) on the event.
//
// `match` is the list of fragments to look for in a calendar event's location
// text, lowercased. `listed` controls whether a place appears in the Where
// panel — a place can be matched for colour-coding without being listed.

export const VENUES = [
  {
    id: 'reception',
    role: 'Dinner & Reception',
    label: 'Ethiopia Hotel No. 2',
    match: ['ethiopia hotel', 'global hotel', 'lancha', 'lancia'],
    mapUrl: 'https://maps.app.goo.gl/BvLFyp5nyGEgoz9k8?g_st=ac',
    address: 'Formerly Global Hotel — Lancha, Addis Ababa',
    coords: '',
    directions: '',
    listed: true,
    tone: 'from-purple-500 to-fuchsia-500',
    ring: 'ring-purple-400/60',
    dot: 'bg-purple-500',
    border: 'border-l-purple-500',
  },
  {
    id: 'church',
    role: 'Church Ceremony',
    label: "Christ's Ambassadors International Church",
    match: ['christ', 'ambassador', 'church', 'gotera'],
    mapUrl: 'https://maps.app.goo.gl/owDbV6ZCBkuQ9iyY8',
    address: 'Gotera, Addis Ababa',
    coords: '',
    directions: '',
    listed: true,
    tone: 'from-amber-500 to-orange-500',
    ring: 'ring-amber-400/60',
    dot: 'bg-amber-500',
    border: 'border-l-amber-500',
  },
  {
    id: 'melse',
    role: 'Melse',
    label: 'Signal Military Club',
    match: ['signal', 'melse', 'mels'],
    mapUrl: 'https://maps.app.goo.gl/fp4GKFkRZ4kcW98i8',
    address: 'Addis Ababa',
    coords: '',
    directions: '',
    listed: true,
    tone: 'from-rose-500 to-pink-500',
    ring: 'ring-rose-400/60',
    dot: 'bg-rose-500',
    border: 'border-l-rose-500',
  },
  {
    id: 'bride-home',
    role: '',
    label: "Bride's Family Home",
    initials: 'BH',
    match: ['bride', 'jacros', 'adebabay'],
    mapUrl: 'https://maps.app.goo.gl/fCx4Q4URWkWu9v3v9',
    address: 'Near Jacros Adebabay, Addis Ababa',
    coords: `9°00'09.0"N 38°49'06.9"E`,
    directions: '',
    listed: true,
    tone: 'from-emerald-500 to-teal-500',
    ring: 'ring-emerald-400/60',
    dot: 'bg-emerald-500',
    border: 'border-l-emerald-500',
  },
  {
    id: 'groom-home',
    role: 'Qileqel',
    label: "Groom's Family Home",
    initials: 'GH',
    // The qileqel happens here too, so a calendar event located by that name
    // colour-codes to this place rather than falling back to a stray colour.
    match: ['groom', 'bisrate', 'gebriel', 'qileqel', 'kilkil'],
    mapUrl: 'https://maps.app.goo.gl/EvJVH6twDSPgiTds8',
    address: 'Bisrate Gebriel, Addis Ababa',
    coords: `8°59'25.5"N 38°43'28.3"E`,
    directions: '',
    listed: true,
    tone: 'from-sky-500 to-indigo-500',
    ring: 'ring-sky-400/60',
    dot: 'bg-sky-500',
    border: 'border-l-sky-500',
  },
];

// Palette for venues not in the registry — indexed by a hash of the location
// text so a given venue keeps the same colour across renders and layouts.
const FALLBACK_TONES = [
  { tone: 'from-violet-500 to-purple-500', ring: 'ring-violet-400/60', dot: 'bg-violet-500', border: 'border-l-violet-500' },
  { tone: 'from-cyan-500 to-sky-500', ring: 'ring-cyan-400/60', dot: 'bg-cyan-500', border: 'border-l-cyan-500' },
  { tone: 'from-lime-500 to-emerald-500', ring: 'ring-lime-400/60', dot: 'bg-lime-500', border: 'border-l-lime-500' },
  { tone: 'from-orange-500 to-rose-500', ring: 'ring-orange-400/60', dot: 'bg-orange-500', border: 'border-l-orange-500' },
];

function hashString(text) {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// ── Pin-located events ──────────────────────────────────────────────────────
//
// A place with no street address gets pinned on the map rather than named, and
// the calendar event then carries the pin's latitude/longitude as its whole
// LOCATION — nothing for the `match` keywords above to bite on. So coordinates
// are compared as numbers instead of as text, which also sidesteps the several
// ways the same pin gets written down: Google copies it as DMS
// (9°00'09.0"N 38°49'06.9"E) from a shared link and as decimals (9.0025,
// 38.8186) from the URL, at whatever precision it feels like.

// ~200 m at this latitude. Loose enough for a pin dropped a couple of houses
// along or a coordinate rounded short, far tighter than the distance between
// any two places on this list.
const COORD_TOLERANCE_DEG = 0.002;

// Both hemisphere letters are optional: a bare decimal pair is read as
// latitude then longitude, which is the order every source writes.
const DMS_RE = /(\d{1,3})\s*°\s*(\d{1,2})\s*['′]\s*(\d{1,2}(?:\.\d+)?)\s*["″]?\s*([NSEW])?/gi;
const DECIMAL_RE =
  /(-?\d{1,3}\.\d+)\s*°?\s*([NS])?(?:\s*[,;]\s*|\s+)(-?\d{1,3}\.\d+)\s*°?\s*([EW])?/i;

// Signs the pair and puts latitude first, whichever order it was written in.
function orient(a, b) {
  const [lat, lng] = /^[EW]$/i.test(a.hemi) ? [b, a] : [a, b];
  const sign = (v) => (/^[SW]$/i.test(v.hemi) ? -Math.abs(v.value) : v.value);
  return { lat: sign(lat), lng: sign(lng) };
}

// Pulls a latitude/longitude pair out of freeform text, in either the DMS or
// the decimal form. Null when there isn't one — a decimal point is required in
// each half of a decimal pair, so ordinary numbers in an address ("Bole Road
// 22, 3rd floor") can't be mistaken for a location.
export function parseCoords(text) {
  const raw = (text || '').trim();
  if (!raw) return null;

  const dms = [...raw.matchAll(DMS_RE)];
  if (dms.length >= 2) {
    const [first, second] = dms.slice(0, 2).map(([, d, m, sec, hemi]) => ({
      value: Number(d) + Number(m) / 60 + Number(sec) / 3600,
      hemi: hemi || '',
    }));
    return orient(first, second);
  }

  const dec = DECIMAL_RE.exec(raw);
  if (!dec) return null;
  const [, lat, latHemi, lng, lngHemi] = dec;
  return orient(
    { value: Number(lat), hemi: latHemi || '' },
    { value: Number(lng), hemi: lngHemi || '' },
  );
}

// The registry's own `coords` strings, parsed once — the Where panel's
// coordinates and the ones matched against here are deliberately the same
// text, so there's no second copy to keep in step.
const VENUE_POINTS = VENUES.map((venue) => ({ venue, point: parseCoords(venue.coords) })).filter(
  (entry) => entry.point,
);

// The registry entry a pin falls on, if any.
export function venueAtCoords(location) {
  const point = parseCoords(location);
  if (!point) return null;
  return (
    VENUE_POINTS.find(
      ({ point: at }) =>
        Math.abs(at.lat - point.lat) <= COORD_TOLERANCE_DEG &&
        Math.abs(at.lng - point.lng) <= COORD_TOLERANCE_DEG,
    )?.venue || null
  );
}

export function googleMapsSearchUrl(query) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

// The places shown in the Where panel, in registry order.
export const LISTED_PLACES = VENUES.filter((v) => v.listed);

// An explicit map link if there is one, otherwise a Maps search for the
// address. Null when there's nothing to search for — better no link than one
// that lands on a search for a place name that means nothing to Maps.
export function placeMapUrl(place) {
  if (place.mapUrl) return place.mapUrl;
  if (place.address) return googleMapsSearchUrl(place.address);
  return null;
}

// Resolves a raw calendar LOCATION string to a venue record. Returns null for
// an empty location so callers can skip the venue treatment entirely.
export function resolveVenue(location) {
  const raw = (location || '').trim();
  if (!raw) return null;

  const haystack = raw.toLowerCase();
  // Named first, then pinned: an event located by coordinates has no name in
  // it to match, so the two can never disagree about the same event.
  const known =
    VENUES.find((v) => v.match.some((m) => haystack.includes(m))) || venueAtCoords(raw);
  if (known) {
    return { ...known, raw, mapUrl: known.mapUrl || googleMapsSearchUrl(raw) };
  }

  // Unknown venue — name it by the first comma-separated segment, which is
  // usually the place itself rather than the street/city tail that follows. A
  // pin that's on no known place is the exception: splitting that at the comma
  // would name the event after half a coordinate.
  const pin = parseCoords(raw);
  const label = pin ? raw : raw.split(',')[0].trim() || raw;
  const palette = FALLBACK_TONES[hashString(label.toLowerCase()) % FALLBACK_TONES.length];
  return {
    id: `venue-${hashString(label.toLowerCase())}`,
    label,
    raw,
    // A tile reading "93" off a coordinate looks like it means something.
    ...(pin ? { initials: '?' } : {}),
    mapUrl: googleMapsSearchUrl(raw),
    ...palette,
  };
}

export function venueInitials(venue) {
  if (!venue) return '?';
  if (venue.initials) return venue.initials;
  return venue.label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}
